import { Stack } from "@mui/material";
// Need to import the route to the next path
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { SelectableBox } from "@src/appV2/redesign/components/SelectableBox";
import { type IconType, Text } from "@src/appV2/redesign/system/ui-components";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";

import { useSelectedWorkType } from "../../hooks/useSelectedWorkType";
import { OnboardingTitle } from "../../SharedTitle";
import { ONBOARDING_STAGES } from "../../util/constants";
import { type OnboardingStepProps } from "../OnboardingStepWrapper";

export const INTERESTED_WORK_TYPE_STORAGE_KEY = "interested-work-type";

export enum WorkTypeOption {
  PERMANENT_PLACEMENTS = "PERMANENT_PLACEMENTS",
  FLEX = "FLEX",
  PERMANENT_AND_FLEX = "PERMANENT_AND_FLEX",
}

interface WorkTypeConfig {
  value: WorkTypeOption;
  iconType: IconType;
  title: string;
  description: string;
}

export function InterestedWorkTypeStepContent(props: OnboardingStepProps) {
  const { onNext } = props;

  const { selectedWorkType, setSelectedWorkType } = useSelectedWorkType();

  const isOptionSelected = selectedWorkType !== undefined;

  const workTypeConfigs: WorkTypeConfig[] = [
    {
      value: WorkTypeOption.PERMANENT_PLACEMENTS,
      iconType: "briefcase",
      title: "Finding a job",
      description:
        "Apply to part time, full time, and PRN positions at a workplace to be their employee",
    },
    {
      value: WorkTypeOption.FLEX,
      iconType: "calendar-navigation",
      title: "Picking up shifts",
      description: "Book individual per diem shifts as an Independent Contractor with Clipboard",
    },
    {
      value: WorkTypeOption.PERMANENT_AND_FLEX,
      iconType: "clipboard",
      title: "Both",
      description:
        "Apply to positions at workplaces to be their employee and work shifts as an Independent Contractor",
    },
  ];

  useLogEffect(APP_V2_USER_EVENTS.ONBOARDING_INTERESTED_WORK_TYPE_PAGE_VIEWED);

  return (
    <>
      <PageContent>
        <OnboardingTitle text="What kind of work are you interested in?" />

        <Stack
          direction="column"
          justifyContent="space-between"
          sx={(theme) => ({
            height: "100%",
            marginTop: theme.spacing(6),
          })}
        >
          <Stack direction="column" spacing={8}>
            <Stack direction="column" spacing={4}>
              {workTypeConfigs.map((config) => (
                <SelectableBox
                  key={config.value}
                  startIconType={config.iconType}
                  variant="secondary"
                  aria-label={config.title}
                  isSelected={selectedWorkType === config.value}
                  sx={{
                    textAlign: "left",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "start",
                    background: (theme) =>
                      selectedWorkType === config.value
                        ? theme.palette.background.tertiary
                        : undefined,
                  }}
                  onToggleSelect={() => {
                    setSelectedWorkType(config.value);
                  }}
                >
                  <Stack direction="column" spacing={2}>
                    <Text semibold variant="h6">
                      {config.title}
                    </Text>
                    <Text variant="body2">{config.description}</Text>
                  </Stack>
                </SelectableBox>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </PageContent>
      <PageFooterWrapper>
        <Button
          fullWidth
          variant="brand"
          disabled={!isOptionSelected}
          size="large"
          onClick={async () => {
            logEvent(APP_V2_USER_EVENTS.ONBOARDING_INTERESTED_WORK_TYPE_SELECTED, {
              selectedOption: selectedWorkType,
            });
            logEvent(APP_V2_USER_EVENTS.ONBOARDING_STEP_COMPLETED, {
              stepCompleted: ONBOARDING_STAGES.INTERESTED_WORK_TYPE,
              selectedOption: selectedWorkType,
            });
            await onNext();
          }}
        >
          Continue
        </Button>
      </PageFooterWrapper>
    </>
  );
}
